import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { fetchBids, fetchUsers } from './api';
import { ClientList } from './components/client-list';
import { UserHeaderControls } from './components/user-header-controls';
import { useClientFilters } from './hooks/use-client-filters';
import { useSessionUser } from './hooks/use-session-user';
import { type ClientFilterType, type SortOption, type User } from './types';
import { clientName, downloadTextFile, formatShortDate, toCsv } from './utils';
import { verificationTone } from './components/verification-badge';

/**
 * «Клієнти» — `Redesign-D-Admin` / `Redesign-D-Admin-Mobile`.
 *
 * The artboard's argument is that a client is read in one line: who they are,
 * how to reach them, when they arrived, whether they are verified, and how much
 * of their bid limit is spent. Desktop gives that a table; mobile gives it a
 * card with the same five facts. Nothing here is decorative — an operator opens
 * this screen to find the one client who is blocked.
 *
 * Loading and error states stay INSIDE the list: the header and the filters are
 * the operator's controls, and blanking them on a slow request loses the search
 * term they just typed.
 */
export function ClientsPage() {
  const [filter, setFilter] = useState<ClientFilterType>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('');

  const usersQuery = useQuery({ queryKey: ['clients'], queryFn: fetchUsers });
  const bidsQuery = useQuery({ queryKey: ['clients', 'bids'], queryFn: fetchBids });
  const { user: sessionUser } = useSessionUser();

  const users = usersQuery.data ?? [];
  const bids = bidsQuery.data ?? [];
  const { filteredUsers, filterCounts } = useClientFilters(users, bids, filter, search || undefined);

  const error = usersQuery.error;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <AccessDenied />;
  }

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('clients.title')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] leading-[13px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:leading-[14px] lg:tracking-[0.14em]">
            <span className="lg:hidden">
              {t('clients.list.shownMobile', {
                shown: filteredUsers.length,
                total: users.length,
              })}
            </span>
            <span className="hidden lg:inline">
              {t('clients.list.summary', {
                registered: filterCounts.all,
                unverified: filterCounts.unverified,
                activeBids: filterCounts.activeBids,
              })}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => exportClientsCsv(filteredUsers, bids)}
          disabled={filteredUsers.length === 0}
          className="hidden h-10 items-center rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover disabled:opacity-50 lg:inline-flex"
        >
          {t('clients.list.export')}
        </button>
      </div>

      {/* ── Controls ────────────────────────────────────────────────────── */}
      <UserHeaderControls
        shown={filteredUsers.length}
        total={users.length}
        searchTerm={search}
        onSearchChange={setSearch}
        filterType={filter}
        onFilterChange={setFilter}
        filterCounts={filterCounts}
        sort={sort}
        onSortChange={setSort}
      />

      {/* ── The list itself ─────────────────────────────────────────────── */}
      <ClientList
        users={filteredUsers}
        bids={bids}
        sessionUserId={sessionUser?._id}
        sort={sort}
        isLoading={usersQuery.isLoading}
        isError={usersQuery.isError}
      />
    </>
  );
}

/**
 * «Експорт CSV» — exactly what is on screen, not the whole collection: the
 * operator filtered for a reason, and an export that ignores the filter is a
 * different question's answer.
 */
function exportClientsCsv(users: User[], bids: { userId: string; status: string }[]): void {
  const header = [
    t('clients.list.columns.client'),
    t('clients.detail.personal.email'),
    t('clients.detail.personal.phone'),
    t('clients.list.columns.registered'),
    t('clients.list.columns.verification'),
    t('clients.detail.limit.active'),
    t('clients.bidLimit.limit'),
  ];

  const rows = users.map((user) => {
    const userBids = bids.filter((bid) => bid.userId === user._id);
    const active = userBids.filter((bid) => ['Won', 'Under Review'].includes(bid.status)).length;
    return [
      clientName(user),
      user.email ?? '',
      user.phone ?? '',
      formatShortDate(user.createdAt),
      verificationTone(user).label,
      String(active),
      String(user.bidLimit ?? 1),
    ];
  });

  const stamp = new Date().toISOString().slice(0, 10);
  downloadTextFile(`clients-${stamp}.csv`, toCsv([header, ...rows]));
}
